import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useSearchParams } from "react-router-dom";
import {
  arraysEqual,
  buildFilterFromKeys as sharedBuildFilterFromKeys,
  extractOmnisearchValue as sharedExtractOmnisearchValue,
  filterKeysEqual,
  FilterKeys,
  INITIAL_FILTER_KEYS,
  parseFilterKeys,
} from "utils/omnisearchFilters";
import {
  addOrUpdateKeyInOmnisearch,
  breakApartOmnisearch,
  parseOmnisearchTokens,
  removeKeyInOmnisearch,
} from "utils/omnisearchUtils";
import { installHistoryInterceptors, subscribeToURL } from "utils/urlInterceptor";

// Re-export the shared symbols so callers can still import them from this module
// (preserves the V2 store's public API while consolidating implementation).
export type { FilterKeys };
export { INITIAL_FILTER_KEYS, parseFilterKeys, filterKeysEqual, installHistoryInterceptors };
export const extractOmnisearchValue = sharedExtractOmnisearchValue;

interface OmnisearchState {
  omnisearch: string;
  filterKeys: FilterKeys;
}

// ============================================================================
// URL-based store using useSyncExternalStore
// ============================================================================

// Cache for referential stability
let cachedState: OmnisearchState = {
  omnisearch: "",
  filterKeys: INITIAL_FILTER_KEYS,
};

/**
 * Get current snapshot by reading URL.
 * Called synchronously during render by useSyncExternalStore.
 * Returns cached state if URL hasn't changed (referential stability).
 */
function getSnapshot(): OmnisearchState {
  const params = new URLSearchParams(window.location.search);
  const omnisearch = params.get("omnisearch") ?? "";

  // Return cached state if omnisearch hasn't changed
  if (omnisearch === cachedState.omnisearch) {
    return cachedState;
  }

  // Parse new filter keys
  const newFilterKeys = parseFilterKeys(omnisearch);

  // Maintain filterKeys reference stability if content is equal
  const filterKeysAreEqual = filterKeysEqual(cachedState.filterKeys, newFilterKeys);
  const stableFilterKeys = filterKeysAreEqual ? cachedState.filterKeys : newFilterKeys;

  // Update cache
  cachedState = {
    omnisearch,
    filterKeys: stableFilterKeys,
  };

  return cachedState;
}

/**
 * Server snapshot - empty state for SSR.
 */
function getServerSnapshot(): OmnisearchState {
  return {
    omnisearch: "",
    filterKeys: INITIAL_FILTER_KEYS,
  };
}

// ============================================================================
// Selector Hooks - Components subscribe to ONLY the state slices they need
// ============================================================================

const DEBOUNCE_MS = 500; // Debounce delay for typing (matches V1)

/**
 * Hook to get the full omnisearch state using useSyncExternalStore.
 * This reads URL synchronously during render, avoiding effect timing issues.
 */
export function useOmnisearchStore(): OmnisearchState {
  return useSyncExternalStore(subscribeToURL, getSnapshot, getServerSnapshot);
}

/**
 * For chart components - only re-renders when filter-relevant state changes.
 * Does NOT re-render when pagination/sorting changes.
 */
export function useChartFilterKeys(): FilterKeys {
  const state = useSyncExternalStore(subscribeToURL, getSnapshot, getServerSnapshot);
  return state.filterKeys;
}

/**
 * For DataGrid - subscribes to full omnisearch string.
 */
export function useFullOmnisearch(): string {
  const state = useSyncExternalStore(subscribeToURL, getSnapshot, getServerSnapshot);
  return state.omnisearch;
}

/**
 * For components that need omnisearch actions.
 * Uses React Router's setSearchParams for proper integration with React's batching.
 */
export function useOmnisearchActions() {
  const [, setSearchParams] = useSearchParams();

  // Per-instance debounce timer. Was previously a module-level `let`, which meant
  // every component that called this hook shared one timer and could cancel each
  // other's pending updates.
  const pendingDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (pendingDebounceTimerRef.current) {
        clearTimeout(pendingDebounceTimerRef.current);
        pendingDebounceTimerRef.current = null;
      }
    };
  }, []);

  const updateSearchParams = useCallback(
    (newOmnisearch: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newOmnisearch) {
            next.set("omnisearch", newOmnisearch);
          } else {
            next.delete("omnisearch");
          }
          return next;
        },
        // Search and filter changes update this page in place. Preventing the
        // router's default scroll reset keeps the reader anchored at the grid.
        { replace: true, preventScrollReset: true }
      );
    },
    [setSearchParams]
  );

  // Debounced setter for typing
  const setOmnisearch = useCallback(
    (value: string) => {
      // Clear any pending debounce
      if (pendingDebounceTimerRef.current) {
        clearTimeout(pendingDebounceTimerRef.current);
      }
      // Schedule debounced URL update
      pendingDebounceTimerRef.current = setTimeout(() => {
        pendingDebounceTimerRef.current = null;
        updateSearchParams(value);
      }, DEBOUNCE_MS);
    },
    [updateSearchParams]
  );

  // Immediate setter for programmatic updates (clear, filter apply, etc.)
  const setOmnisearchImmediate = useCallback(
    (value: string) => {
      // Clear any pending debounce to avoid stale updates
      if (pendingDebounceTimerRef.current) {
        clearTimeout(pendingDebounceTimerRef.current);
        pendingDebounceTimerRef.current = null;
      }
      updateSearchParams(value);
    },
    [updateSearchParams]
  );

  // Edit multiple key-value pairs
  const editKeyValues = useCallback(
    (keyValues: [string, string][]) => {
      const current = getSnapshot().omnisearch;
      let updated = current;
      for (const [key, value] of keyValues) {
        updated = addOrUpdateKeyInOmnisearch(updated, key, value);
      }
      updateSearchParams(updated);
    },
    [updateSearchParams]
  );

  // Remove a key from omnisearch
  const removeKey = useCallback(
    (key: string) => {
      const current = getSnapshot().omnisearch;
      const updated = removeKeyInOmnisearch(current, key);
      updateSearchParams(updated);
    },
    [updateSearchParams]
  );

  return {
    setOmnisearch,
    setOmnisearchImmediate,
    editKeyValues,
    removeKey,
  };
}

// ============================================================================
// Helper to convert FilterKeys to GraphQL filter format
// ============================================================================

/**
 * Convert FilterKeys to the GraphQL WasteSubmissionsSummaryFilter format.
 */
export const buildFilterFromKeys = sharedBuildFilterFromKeys;

// ============================================================================
// Exports for testing
// ============================================================================

/**
 * Reset the cache - useful for testing.
 */
export function __resetCache() {
  cachedState = {
    omnisearch: "",
    filterKeys: INITIAL_FILTER_KEYS,
  };
}

/**
 * Exports for testing internal functions.
 */
export const __testing = {
  parseFilterKeys,
  filterKeysEqual,
  arraysEqual,
  parseOmnisearchTokens,
  breakApartOmnisearch,
  addOrUpdateKeyInOmnisearch,
  removeKeyInOmnisearch,
};
